import { FC, Fragment, useCallback, useEffect, useState, useContext } from 'react';
import * as ApiModels from '@typings/api-models';
import { CONTEXT_TYPE } from '@typings/api-models';
import TextInput from '@components/FormFields/TextInput';
import Spacer from '@components/Spacer';
import EmailInput from '@components/FormFields/EmailInput';
import Switch from '@components/DesignLibrary/Atoms/Switch';
import Button from '@components/DesignLibrary/Button';
import useNoodleApi from '@hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import TeamsContext from '@providers/Teams/TeamsContext';
import ProgressIndicator from '@components/ProgressIndicator';
import { getContextValues } from '@/helpers/expressions/filterWorkflowInvoicesByContextExpressions';
import evaluateExpression from '@/helpers/evaluateExpression';
import CustomSelect from '@/components/FormFields/CustomSelect';
import { PlusCircle } from '@phosphor-icons/react';
import AddContextSelection from './AddContextSelection';
import s from './InitiateCaseModal.module.scss';
import { ContextData } from './types';
import CreateCompany from './CreateCompany';

type ThisContext = Pick<ApiModels.WorkflowContext, 'id' | 'type' | 'label' | 'slug' | 'options'> & {
  expression?: ApiModels.Expression | null;
};

type Props = {
  customer: Pick<ApiModels.Person, 'id' | 'name' | 'email'>;
  workflow: Pick<ApiModels.Workflow, 'id'> & {
    contexts: Array<ThisContext>;
  };
  onSubmit: (contextData: ContextData[]) => void;
  initialContext: ContextData[] | null;
  associatedUserWorkflowId?: string;
};

const PAGE_SIZE = 25;

const isButtonDisabled = (contextData: ContextData[], customer: { email: string | null }): boolean => {
  for (let i = 0; i < contextData.length; i += 1) {
    if (!contextData[i].context.isOptional) {
      if (contextData[i].context.type === CONTEXT_TYPE.PERSON) {
        if (
          !(contextData[i].data as ApiModels.CreatePersonContextData).isCustomer
          && (!(contextData[i].data as ApiModels.CreatePersonContextData).name
            || !(contextData[i].data as ApiModels.CreatePersonContextData).email
            || (contextData[i].data as { emailError: string | null }).emailError
            || (contextData[i].data as { nameError: string | null }).nameError
            || (contextData[i].data as { email: string }).email === customer.email)
        ) {
          return true;
        }
      } else if (contextData[i].context.type === CONTEXT_TYPE.ADDITIONAL_INFO) {
        if (!(contextData[i].data as ApiModels.CreateInfoContextData).value) {
          return true;
        }
      } else if (contextData[i].context.type === CONTEXT_TYPE.SELECTION) {
        if (!(contextData[i].data as ApiModels.CreateSelectionContextData).slug) {
          return true;
        }
      }
    }
  }
  return false;
};

const AddContext: FC<Props> = ({ associatedUserWorkflowId, customer, workflow, onSubmit, initialContext }) => {
  const { getData: getInitialContextData } = useNoodleApi(tsClient.workflows.getPrefilledContextData);

  const [isFetchingContextData, setIsFetchingContextData] = useState(false);
  const [contextData, setContextData] = useState<Array<ContextData | unknown>>(
    initialContext
      || workflow.contexts.map(c => ({
        context: c,
        data: {},
      })),
  );

  const [companyOptions, setCompanyOptions] = useState<{ title: string; value: string }[]>([]);
  const [isCreatingCompany, setIsCreatingCompany] = useState(false);

  const { creatorId } = useContext(TeamsContext);

  const { getData: getCompaniesFn } = useNoodleApi(tsClient.getCompanies);

  // TODO: only call this function when the context requires it
  const fetchCompaniesOptions = useCallback(
    async ({ page, search = '' }: { page: number; search?: string }) => {
      if (creatorId) {
        const getResponse = await getCompaniesFn({ creatorId, page, perPage: PAGE_SIZE, search });

        if (getResponse.data) {
          const selectedCompany = contextData.find(context => (context as ContextData).context.slug === 'company-name') as ContextData | undefined;

          const selectedCompanyData = selectedCompany?.data as ApiModels.CreateInfoContextData | undefined;

          const selectedOption = selectedCompanyData?.value && selectedCompanyData?.companyId
            ? { title: selectedCompanyData.value, value: selectedCompanyData.companyId }
            : null;

          const fetchedOptions = getResponse.data.items.map(company => ({
            title: company.name,
            value: company.id,
          }));

          const finalOptions = selectedOption
            ? [selectedOption, ...fetchedOptions.filter(option => option.value !== selectedOption.value)]
            : fetchedOptions;

          setCompanyOptions(finalOptions);
        }
      }
    },
    [creatorId, getCompaniesFn, contextData],
  );

  const handleCompanySelection = (company: { title: string; value: string }): void => {
    setContextData(prev =>
      prev.map(context => {
        if ((context as ContextData).context.slug === 'company-name') {
          return {
            ...(context as ContextData),
            data: {
              companyId: company.value,
              value: company.title,
            },
          };
        }
        return context;
      }),
    );
  };

  const handleSelect = (companyId: string, error: string | null): void => {
    if (!error && companyId) {
      const selectedCompanyObj = companyOptions.find(company => company.value === companyId) || { title: companyId, value: companyId };

      handleCompanySelection(selectedCompanyObj);
    }
  };

  const handleSearch = useCallback(
    (search: string) => {
      fetchCompaniesOptions({ page: 1, search });
    },
    [fetchCompaniesOptions],
  );

  const evaluateContextConditions = useCallback(
    (context: ContextData): boolean => {
      const { expression } = context.context;
      if (expression) {
        const variables: Record<string, unknown> = {};
        expression.variables.forEach(variable => {
          variables[variable.variableName] = getContextValues(contextData as ContextData[]);
        });

        const result = evaluateExpression({
          expression: expression.expression,
          id: 'AddContext',
          variables,
        }) ?? false;
        return Boolean(result);
      }
      return true;
    },
    [contextData],
  );

  useEffect(() => {
    if (associatedUserWorkflowId && (initialContext?.length || 0) === 0) {
      const getData = async (): Promise<void> => {
        const { data } = await getInitialContextData({
          associatedUserWorkflowId,
          id: workflow.id,
        });
        if (data) {
          setContextData(prev =>
            prev.map(context => {
              const { slug } = (context as ContextData).context;
              if (slug && data[slug]) {
                return {
                  ...(context as ContextData),
                  data: {
                    value: data[slug],
                  },
                };
              }
              return context;
            }),
          );
        }
        setIsFetchingContextData(false);
      };
      getData();
    } else {
      setIsFetchingContextData(false);
    }
  }, [getInitialContextData, workflow, associatedUserWorkflowId]);

  const handleSubmit = useCallback(() => {
    const contextToUse = (contextData as ContextData[]).filter(evaluateContextConditions);
    onSubmit(contextToUse);
  }, [contextData, evaluateContextConditions]);

  const setContextDataField = (contextId: string, field: string) => (data: string | boolean | null) => {
    setContextData(prev =>
      prev.map(context => {
        if ((context as ContextData).context.id === contextId) {
          return {
            ...(context as ContextData),
            data: {
              ...(context as ContextData).data,
              [field]: data,
            },
          };
        }
        return context;
      }),
    );
  };

  useEffect(() => {
    fetchCompaniesOptions({ page: 1 });
  }, [fetchCompaniesOptions, contextData]);

  const disableButton = isButtonDisabled((contextData as ContextData[]).filter(evaluateContextConditions), customer);

  return (
    <div className={s.contexts}>
      {isFetchingContextData
        ? (
          <ProgressIndicator isCentered />
        )
        : (
          <>
            {!isCreatingCompany
              ? (
                <>
                  {(contextData as ContextData[]).filter(evaluateContextConditions).map(contextDataItem => (
                    <Fragment key={contextDataItem.context.id}>
                      <div className={s.context} key={contextDataItem.context.id}>
                        <p className="body-md">
                          {contextDataItem.context.label}{' '}
                          {contextDataItem.context.isOptional && <span style={{ color: 'var(--color-gray-75)' }}>(Optional)</span>}
                        </p>
                        {contextDataItem.context.type === CONTEXT_TYPE.PERSON && (
                          <div>
                            <Switch
                              name={`isCustomer-${contextDataItem.context.id}`}
                              isChecked={(contextDataItem.data as ApiModels.CreatePersonContextData).isCustomer}
                              label={`${customer.name} is the ${contextDataItem.context.label}`}
                              onChange={() =>
                                setContextDataField(
                                  contextDataItem.context.id,
                                  'isCustomer',
                                )(!((contextDataItem.data as ApiModels.CreatePersonContextData).isCustomer || false))
                              }
                            />
                            {!(contextDataItem.data as ApiModels.CreatePersonContextData).isCustomer && (
                              <>
                                <Spacer size="16px" />
                                <TextInput
                                  id={`name-${contextDataItem.context.id}`}
                                  value={(contextDataItem.data as ApiModels.CreatePersonContextData).name}
                                  onChange={setContextDataField(contextDataItem.context.id, 'name')}
                                  label="Name"
                                  placeholder="Enter name"
                                  autoComplete={'name'}
                                  onError={setContextDataField(contextDataItem.context.id, 'nameError')}
                                  required
                                />
                                <EmailInput
                                  id={`email-${contextDataItem.context.id}`}
                                  value={(contextDataItem.data as ApiModels.CreatePersonContextData).email || ''}
                                  onChange={setContextDataField(contextDataItem.context.id, 'email')}
                                  label="Email"
                                  placeholder="Enter email"
                                  error={
                                    (contextDataItem.data as ApiModels.CreatePersonContextData).email === customer.email
                                      ? 'Email must not match customer email'
                                      : undefined
                                  }
                                  onError={setContextDataField(contextDataItem.context.id, 'emailError')}
                                  required
                                />
                              </>
                            )}
                          </div>
                        )}
                        {contextDataItem.context.type === CONTEXT_TYPE.ADDITIONAL_INFO && (
                          <div>
                            {contextDataItem.context.slug === 'company-name'
                              ? (
                                <div>
                                  <CustomSelect
                                    id={`value-${contextDataItem.context.id}`}
                                    label={contextDataItem.context.label}
                                    value={(contextDataItem.data as ApiModels.CreateInfoContextData).companyId ?? ''}
                                    options={companyOptions}
                                    onChange={handleSelect}
                                    withFilter
                                    onSearch={handleSearch}
                                  />
                                  <Spacer size={6} />
                                  <Button
                                    size="md"
                                    variant="secondary"
                                    fullWidth
                                    onClick={() => {
                                      setIsCreatingCompany(true);
                                    }}
                                  >
                                    <div className={s.createCompanyButton}>
                                      <PlusCircle size={20} weight="fill" color="var(--color-noodle)" />
                                      <p className="body-sm-bold" style={{ color: 'var(--color-noodle)' }}>
                                  Create Company
                                      </p>
                                    </div>
                                  </Button>
                                </div>
                              )
                              : (
                                <TextInput
                                  id={`value-${contextDataItem.context.id}`}
                                  value={(contextDataItem.data as ApiModels.CreateInfoContextData).value}
                                  onChange={setContextDataField(contextDataItem.context.id, 'value')}
                                  label={contextDataItem.context.label}
                                  placeholder={`Enter ${contextDataItem.context.label}`}
                                  required={!contextDataItem.context.isOptional}
                                />
                              )}
                          </div>
                        )}
                        {contextDataItem.context.type === CONTEXT_TYPE.SELECTION && (
                          <AddContextSelection
                            context={contextDataItem.context}
                            value={(contextDataItem.data as ApiModels.CreateSelectionContextData).slug}
                            onChange={key => {
                              const label = contextDataItem.context.options.find(opt => opt.slug === key)?.label;
                              setContextDataField(contextDataItem.context.id, 'slug')(key);
                              if (label) {
                                setContextDataField(contextDataItem.context.id, 'label')(label);
                              }
                            }}
                          />
                        )}
                      </div>
                      <Spacer size="16px" />
                    </Fragment>
                  ))}
                  <div className={s.buttonContainer} style={{ paddingLeft: 0, paddingRight: 0, paddingTop: 0 }}>
                    <Button disabled={disableButton} variant="primary" size="md" fullWidth onClick={handleSubmit}>
                  Next
                    </Button>
                  </div>
                </>
              )
              : (
                <CreateCompany onClose={() => setIsCreatingCompany(false)} onCompanyCreated={handleCompanySelection} />
              )}
          </>
        )}
    </div>
  );
};

export default AddContext;
